import { Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { NgEntityService } from '@datorama/akita-ng-entity-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';

import { CrowdFlowROI } from '../../model/crowd-flow-roi';
import { HttpOptions, UpdateActionType } from '../../service/generic-akita-http.service';
import { WebsocketService } from '../../service/websocket.service';
import { CrowdFlowROIState, CrowdFlowROIStore } from '../state/crowd-flow-roi.store';
import { AkitaHTTPCrowdFlowROIService } from './akita-http-crowdflow-roi.service';
import { UrlConfig } from '../../model/urlconfig';


@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class CrowdFlowROIService extends NgEntityService<CrowdFlowROIState> implements UrlConfig {

  constructor(
    protected store: CrowdFlowROIStore,
    private httpCrowdFlowRoiService: AkitaHTTPCrowdFlowROIService,
    private webSocketService: WebsocketService,
  ) {
    super(store);

    this.webSocketService.getDataStream('CrowdFlowROI')
      .pipe(untilDestroyed(this))
      .subscribe((data: CrowdFlowROI[]) => {
        for (const roi of data) {
          if (!roi.active) { this.store.remove(roi.id); }
          else { this.store.upsert(roi.id, roi); }
        }
      });
  }
  updateEntitiesUrl(newUrl: string): void {
    this.httpCrowdFlowRoiService.setEntitiesUrl(newUrl);
  }

  getAll() {
    this.httpCrowdFlowRoiService.getAll()
      .pipe(map(res => res.filter(crowdFlow => crowdFlow.active === true)))
      .subscribe(rois => this.store.upsertMany(rois));
  }

  getById(id: ID) {
    this.httpCrowdFlowRoiService.loadById(id).subscribe(roi => this.store.add(roi));
  }

  addToLocalStore(crowdFlowROI: CrowdFlowROI) {
    this.store.add(crowdFlowROI);
  }

  addToHub(crowdFlowROI: CrowdFlowROI) {
    this.store.add(crowdFlowROI);
    const options = {
      params: {
        options: 'keyValues'
      }
    };
    this.httpCrowdFlowRoiService
      .update(crowdFlowROI, UpdateActionType.APPEND, options)
      .subscribe(
        data => console.log('Updated ROI', crowdFlowROI),
        error => console.log('Error inserting in Hub', error)
      );
  }

  /**
   * Deletes the ROI from the store and sets its status `active` to false in the Hub.
   */
  deleteRoi(roi: CrowdFlowROI) {
    this.store.remove(roi.id);

    const copy = { ...roi };
    copy.active = false;

    const options: HttpOptions = {
      params: {
        options: 'keyValues'
      }
    };
    this.httpCrowdFlowRoiService
      .update(copy, UpdateActionType.APPEND, options)
      .subscribe(data => console.log('updated for delete roi', data));
  }

  nameRoi(roi: CrowdFlowROI, name: string) {
    this.store.update(roi.id, { displayName: name });

    const options: HttpOptions = {
      params: {
        options: 'keyValues'
      }
    };
    this.httpCrowdFlowRoiService
      .update({ ...roi, displayName: name }, UpdateActionType.APPEND, options)
      .subscribe(
        data => console.log('Updated ROI', data),
        error => console.log('Error inserting in Hub', error)
      );
  }

  nameEdge(edgeId: string | number, roi: CrowdFlowROI, name: string) {
    const edgeNames = { ...roi.edgeNames };
    edgeNames[edgeId] = name;
    this.store.update(roi.id, { edgeNames });

    const options: HttpOptions = {
      params: {
        options: 'keyValues'
      }
    };
    this.httpCrowdFlowRoiService
      .update({ ...roi, edgeNames }, UpdateActionType.APPEND, options)
      .subscribe(
        data => console.log('Updated ROI', data),
        error => console.log('Error inserting in Hub', error)
      );
  }

}
